import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Unauthorized from '../general/Unauthorized';
import {
    combinarPacientesYEmpresas,
    filtrarPacientesParaModal,
    getNombreClienteFacturacion,
} from '../../../utils/busquedaPacienteFacturacion';

/** Solo desarrollo: poner en true para ver errores en consola. */
const DEBUG_CREAR_FACTURA_RIPS = false;
const crearFacturaRipsDebug = (...args) => {
    if (DEBUG_CREAR_FACTURA_RIPS) {
        console.error(...args);
    }
};

function CrearFacturaRips() {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);
    
    // IDs de RIPS seleccionados (vienen desde RipsConvenios)
    const [ripsIds, setRipsIds] = useState([]);
    
    // Datos del formulario
    const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
    const [vencimiento, setVencimiento] = useState(new Date().toISOString().split('T')[0]);
    const [profesional, setProfesional] = useState('');
    const [sede, setSede] = useState('');
    const [prefijo, setPrefijo] = useState('');
    const [numeroFactura, setNumeroFactura] = useState('');
    const [metodoPago, setMetodoPago] = useState('10');
    const [tipoPago, setTipoPago] = useState('contado');
    const [observaciones, setObservaciones] = useState('');
    const [contractNumber, setContractNumber] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    
    // Datos del paciente (se cargan automáticamente)
    const [pacienteId, setPacienteId] = useState('');
    const [nombrePaciente, setNombrePaciente] = useState('');
    const [tipoPaciente, setTipoPaciente] = useState('CC');
    const [documentoPaciente, setDocumentoPaciente] = useState('');
    const [codigoResponsabilidad, setCodigoResponsabilidad] = useState('No responsable');
    const [codigoRegimen, setCodigoRegimen] = useState('No responsable de IVA');
    const [codigoImpuesto, setCodigoImpuesto] = useState('No causa');
    
    // Productos (servicios de los RIPS)
    const [productos, setProductos] = useState([]);
    const [totalBruto, setTotalBruto] = useState(0);
    const [totalDescuento, setTotalDescuento] = useState(0);
    const [totalAjuste, setTotalAjuste] = useState(0);
    const [totalIVA, setTotalIVA] = useState(0);
    const [totalPagar, setTotalPagar] = useState(0);

    // Funciones de cálculo con ajuste (misma lógica que presupuestos)
    const calcularBaseConDescuentoItem = (item) => {
        const precio = parseFloat(item.precio) || 0;
        const cantidad = parseFloat(item.cantidad) || 0;
        const porcentaje = parseFloat(item.porcentaje) || 0;
        const precioBase = precio * cantidad;
        const descuento = precioBase * (porcentaje / 100);
        return precioBase - descuento;
    };

    const calcularBaseAjustadaItem = (item) => {
        const baseConDescuento = calcularBaseConDescuentoItem(item);
        const ajuste = parseFloat(item.ajuste) || 0;
        return baseConDescuento + ajuste;
    };

    const calcularTotalItem = (item) => {
        const baseAjustada = calcularBaseAjustadaItem(item);
        const impuestoPct = parseFloat(item.impuesto) || 0;
        const impuesto = baseAjustada * (impuestoPct / 100);
        return baseAjustada + impuesto;
    };
    
    // Datos de API
    const [profesionales, setProfesionales] = useState([]);
    const [parametrosFev, setParametrosFev] = useState([]);
    const [impuestos, setImpuestos] = useState([]);
    
    // Modal de pacientes
    const [showModalPaciente, setShowModalPaciente] = useState(false);
    const [pacientes, setPacientes] = useState([]);
    const [busquedaPaciente, setBusquedaPaciente] = useState('');
    const [paginaActual, setPaginaActual] = useState(1);
    const pacientesPorPagina = 10;

    useEffect(() => {
        // Validar permisos primero
        let userdata = [];
        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        // Verificar si tiene permiso para administrar RIPS
        const tienePermiso = userdata.up && userdata.up.includes('administrarRips');
        
        if (!tienePermiso) {
            setHasPermission(false);
            return; // Detener ejecución si no tiene permisos
        }

        setHasPermission(true);

        // Obtener IDs de RIPS desde la URL
        const params = new URLSearchParams(location.search);
        const ids = params.get('rips_ids');
        
        if (ids) {
            const idsArray = ids.split(',').map(id => parseInt(id));
            setRipsIds(idsArray);
            cargarDatosIniciales(idsArray);
        } else {
            alert('No se indicaron RIPS en el enlace. Vuelva al listado y seleccione los registros que desea facturar.');
            window.location.href = '/#/rips-convenios';
        }
    }, [location.search]);

    // Misma lógica que CrearFactura: al cambiar profesional (o cargar FEV), asignar resolución activa y consecutivo.
    useEffect(() => {
        if (!profesional || !parametrosFev.length) {
            if (!profesional) {
                setPrefijo('');
                setNumeroFactura('');
            }
            return;
        }
        const lista = parametrosFev.filter(
            (param) =>
                param.tipo === 'Factura Electronica' &&
                param.users_id == profesional &&
                param.estado == 1
        );
        if (!lista.length) {
            setPrefijo('');
            setNumeroFactura('');
            return;
        }
        const actualValido = lista.some((p) => String(p.id) === String(prefijo));
        const elegido = actualValido ? lista.find((p) => String(p.id) === String(prefijo)) : lista[0];
        setPrefijo(String(elegido.id));
        setNumeroFactura(
            elegido.numero_factura != null ? String(elegido.numero_factura) : ''
        );
    }, [profesional, parametrosFev, prefijo]);

    const cargarDatosIniciales = async (idsArray) => {
        let userdata = JSON.parse(localStorage.getItem('userdata'));
        
        try {
            // Cargar datos generales
            const [profResponse, fevResponse, impuestosResponse, respPacientes, respEmpresas] = await Promise.all([
                axios.get('/usuarios?uid=' + userdata.user_id),
                axios.get('/parametros-fev?uid=' + userdata.user_id),
                axios.get('/administrador-impuestos'), // Tabla maestra - sin filtro por client_id
                axios.get('/pacientes', { params: { uid: userdata.user_id } }),
                axios.get('/pacientes', { params: { uid: userdata.user_id, solo_empresas: true } }),
            ]);
            
            setProfesionales(profResponse.data);
            setParametrosFev(fevResponse.data);
            setImpuestos(impuestosResponse.data);
            setPacientes(combinarPacientesYEmpresas(respPacientes, respEmpresas));
            
            // Cargar datos de los RIPS seleccionados
            const ripsResponse = await axios.get('/rips-convenio/detalle', {
                params: {
                    rips_ids: idsArray.join(','),
                },
            });
            
            const ripsData = ripsResponse.data;
            
            if (ripsData.length === 0) {
                alert('No se encontraron servicios para los RIPS seleccionados. Revise el listado o los filtros e intente de nuevo.');
                return;
            }
            
            // Cargar datos iniciales desde el primer RIPS
            const primerRips = ripsData[0];
            setProfesional(
                primerRips.users_id != null && primerRips.users_id !== ''
                    ? String(primerRips.users_id)
                    : ''
            );
            setSede(
                primerRips.sede_id != null && primerRips.sede_id !== ''
                    ? String(primerRips.sede_id)
                    : ''
            );
            
            // NO cargar paciente automáticamente - el usuario debe seleccionarlo manualmente
            // Esto permite facturar a una entidad diferente (convenio) en lugar del paciente del RIPS
            
            // Convertir RIPS a productos
            const productosArray = ripsData.map(rips => ({
                id: rips.id,
                rips_id: rips.id,
                id_listadeprecios: rips.listaprecios_id,
                codigo: rips.codigo,
                descripcion: rips.nombre_servicio,
                cantidad: 1,
                precio: parseFloat(rips.valor_servicio),
                porcentaje: 0,
                impuesto: 0,
                ajuste: 0,
                total: parseFloat(rips.valor_servicio)
            }));
            
            setProductos(productosArray);
            calcularTotales(productosArray);
            setLoading(false);
            
        } catch (error) {
            crearFacturaRipsDebug('CrearFacturaRips: error al cargar datos', error);
            const st = error.response?.status;
            const msg = error.response?.data?.message || error.message;
            if (st === 403) {
                alert(
                    (msg || 'No tiene permiso para ver estos RIPS o ya no están disponibles.') +
                        '\n\nVolviendo al listado de RIPS por convenio.'
                );
                window.location.href = '/#/rips-convenios';
            } else {
                alert(
                    'No se pudo cargar esta pantalla. ' +
                        (msg ? msg + ' ' : '') +
                        'Compruebe su conexión e intente de nuevo.'
                );
            }
            setLoading(false);
        }
    };

    const handlePrefijoChange = (e) => {
        const val = e.target.value;
        const selectedPrefijo = getPrefijosFiltrados().find((p) => String(p.id) === String(val));
        setPrefijo(val ? String(val) : '');
        if (selectedPrefijo && selectedPrefijo.numero_factura != null) {
            setNumeroFactura(String(selectedPrefijo.numero_factura));
        } else if (!val) {
            setNumeroFactura('');
        }
    };

    const getPrefijosFiltrados = () => {
        return parametrosFev.filter(
            (param) =>
                param.tipo === 'Factura Electronica' &&
                param.users_id == profesional &&
                param.estado == 1
        );
    };

    const actualizarProducto = (id, campo, valor) => {
        const nuevosProductos = productos.map(p => {
            if (p.id === id) {
                if (campo === 'ajuste') {
                    // Permite escritura temporal de "-" en el input sin forzar 0
                    const productoActualizado = { ...p, ajuste: String(valor ?? '') };
                    const ajusteNumerico = parseFloat(productoActualizado.ajuste);
                    const baseConDescuento = calcularBaseConDescuentoItem(productoActualizado);
                    const baseBruta = (parseFloat(productoActualizado.precio) || 0) * (parseFloat(productoActualizado.cantidad) || 1);
                    const ajusteMinimo = -baseConDescuento; // No puede dejar el total en negativo
                    const ajusteMaximo = baseBruta; // No puede ser mayor al precio unitario x cantidad
                    if (Number.isFinite(ajusteNumerico)) {
                        if (ajusteNumerico < ajusteMinimo) {
                            productoActualizado.ajuste = ajusteMinimo;
                        } else if (ajusteNumerico > ajusteMaximo) {
                            productoActualizado.ajuste = ajusteMaximo;
                        }
                    }
                    productoActualizado.total = calcularTotalItem(productoActualizado);
                    return productoActualizado;
                }
                const productoActualizado = { ...p, [campo]: parseFloat(valor) || 0 };
                productoActualizado.total = calcularTotalItem(productoActualizado);
                return productoActualizado;
            }
            return p;
        });
        setProductos(nuevosProductos);
        calcularTotales(nuevosProductos);
    };

    const calcularTotales = (productosArray) => {
        let bruto = 0;
        let totalDescuentos = 0;
        let totalAjustes = 0;
        let totalImpuestos = 0;
        let pagar = 0;

        productosArray.forEach(producto => {
            const precioBase = producto.precio * producto.cantidad;
            const descuento = precioBase * (producto.porcentaje / 100);
            const ajuste = parseFloat(producto.ajuste) || 0;
            const baseConDescuento = precioBase - descuento;
            const baseAjustada = baseConDescuento + ajuste;
            const impuesto = baseAjustada * (producto.impuesto / 100);

            bruto += precioBase;
            totalDescuentos += descuento;
            totalAjustes += ajuste;
            totalImpuestos += impuesto;
            pagar += parseFloat(producto.total ?? (baseAjustada + impuesto)) || 0;
        });

        setTotalBruto(bruto);
        setTotalDescuento(totalDescuentos);
        setTotalAjuste(totalAjustes);
        setTotalIVA(totalImpuestos);
        setTotalPagar(pagar);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (productos.length === 0) {
            alert('Debe tener al menos un servicio en la tabla para generar la factura.');
            return;
        }

        if (tipoPago === 'credito' && fecha === vencimiento) {
            alert('En venta a crédito, la fecha de vencimiento debe ser distinta a la fecha del documento.');
            return;
        }

        let userdata = JSON.parse(localStorage.getItem('userdata'));

        const facturaData = {
            parametrosfev_id: parseInt(prefijo),
            users_id: parseInt(profesional),
            paciente_id: parseInt(pacienteId),
            sedes_id: sede ? parseInt(sede, 10) : null,
            fecha_emision: fecha,
            fecha_vence: vencimiento,
            numero_factura: numeroFactura,
            forma_pago: metodoPago,
            tipo_pago: tipoPago,
            valor_bruto: parseFloat(totalBruto),
            valor_total: parseFloat(totalPagar),
            observaciones: observaciones || '',
            contract_number: contractNumber || null,
            policy_number: policyNumber || null,
            codigo_responsabilidad: codigoResponsabilidad,
            codigo_regimen: codigoRegimen,
            codigo_impuesto: codigoImpuesto,
            rips_ids: ripsIds, // IDs de RIPS para actualizar
            detalle: productos.map(producto => ({
                id_listadeprecios: parseInt(producto.id_listadeprecios),
                cantidad: parseInt(producto.cantidad),
                precio_unitario: parseFloat(producto.precio),
                porcentaje: parseFloat(producto.porcentaje),
                impuesto: parseFloat(producto.impuesto),
                ajuste: parseFloat(producto.ajuste) || 0,
                total: parseFloat(producto.total)
            }))
        };

        try {
            const response = await axios.post('/rips-convenio/generar-factura', facturaData);
            
            if (response.status === 200 || response.status === 201) {
                const facturaId = response.data.factura_id;
                alert(
                    `Factura creada: ${response.data.prefijo}-${response.data.numero_factura}. ` +
                        'Puede enviarla a la DIAN en el siguiente paso si lo desea.'
                );
                
                const enviarDian = window.confirm(
                    '¿Desea enviar esta factura a la DIAN ahora?\n\nSe generará el documento electrónico ante la DIAN.'
                );
                
                if (enviarDian) {
                    try {
                        const envioResponse = await axios.post(`/factura/${facturaId}/enviar-dian`, {});
                        
                        if (envioResponse.data.success) {
                            alert('La factura se envió correctamente a la DIAN.');
                        } else {
                            alert(
                                'No se pudo completar el envío a la DIAN. ' +
                                    (envioResponse.data.message || 'Intente de nuevo o revise el estado de la factura.')
                            );
                        }
                    } catch (envioError) {
                        crearFacturaRipsDebug('CrearFacturaRips: error envío DIAN', envioError);
                        alert(
                            'No se pudo enviar la factura a la DIAN. ' +
                                (envioError.response?.data?.message || envioError.message || 'Intente más tarde.')
                        );
                    }
                }
                
                // Redirigir al listado de facturas
                window.location.href = `/#/informacion-factura/${facturaId}`;
            }
        } catch (error) {
            crearFacturaRipsDebug('CrearFacturaRips: error al guardar factura', error);
            const st = error.response?.status;
            const msg = error.response?.data?.message || error.message;
            if (st === 403) {
                alert(
                    (msg || 'No tiene permiso para esta operación o los datos ya no son válidos.') +
                        '\n\nVolviendo al listado de RIPS por convenio.'
                );
                window.location.href = '/#/rips-convenios';
            } else {
                alert('No se pudo crear la factura. ' + (msg || 'Revise los datos e intente de nuevo.'));
            }
        }
    };

    const formatearValor = (valor) => {
        if (!valor || valor === 0) return '-';
        return '$' + Math.round(parseFloat(valor)).toLocaleString('es-CO');
    };

    const getNombreCliente = getNombreClienteFacturacion;

    const getPacientesFiltrados = () =>
        filtrarPacientesParaModal(pacientes, busquedaPaciente);

    const getPacientesPaginados = () => {
        const pacientesFiltrados = getPacientesFiltrados();
        const inicio = (paginaActual - 1) * pacientesPorPagina;
        const fin = inicio + pacientesPorPagina;
        return pacientesFiltrados.slice(inicio, fin);
    };

    const getTotalPaginas = () => {
        return Math.ceil(getPacientesFiltrados().length / pacientesPorPagina);
    };

    const seleccionarPaciente = (paciente) => {
        const nombreCompleto = getNombreClienteFacturacion(paciente);
        setPacienteId(paciente.id);
        setNombrePaciente(nombreCompleto);
        setTipoPaciente(paciente.tipo_documento || 'CC');
        setDocumentoPaciente(paciente.numero_documento || '');
        setCodigoResponsabilidad(paciente.codigo_responsabilidad || 'No Responsable');
        setCodigoRegimen(paciente.codigo_regimen || 'No Responsable de IVA');
        setCodigoImpuesto(paciente.codigo_impuesto || 'No Causa');
        setShowModalPaciente(false);
    };

    if (loading) {
        return (
            <div className="hold-transition">
                <Header />
                <Aside activelink="indicators" />
                <div className="content-wrapper">
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="text-center">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Cargando...</span>
                                </div>
                                <p>Cargando Datos...</p>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="hold-transition factura-page">
            <style>{`
                .factura-page { font-size: 12.5px; }
                .factura-page .content-header h1 {
                    font-size: 1.9rem !important;
                    font-weight: 800 !important;
                    margin-bottom: 0 !important;
                }
                .factura-page label {
                    font-size: 0.76rem;
                    font-weight: 700;
                    color: #6c757d;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: .03rem;
                }
                .factura-page .form-control {
                    font-size: 0.82rem !important;
                    padding: 0.38rem 0.68rem !important;
                    min-height: 36px;
                }
                .factura-page .btn { font-size: 0.82rem !important; }
                .factura-soft-card {
                    border-radius: 16px !important;
                    border: 1px solid rgba(0,0,0,0.05) !important;
                    box-shadow: 0 10px 24px rgba(0,0,0,0.04) !important;
                }
                .factura-soft-header {
                    background: #fff;
                    border-bottom: 1px solid rgba(0,0,0,0.06);
                }
                .factura-main-title {
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 1rem;
                    font-weight: 800;
                    color: #323a45;
                }
                .factura-main-title .title-icon {
                    width: 28px;
                    height: 28px;
                    border-radius: 9px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(23,162,184,0.1);
                    color: #17a2b8;
                    font-size: 0.85rem;
                }
                .factura-section-title {
                    margin: 0 0 14px 0;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 0.95rem;
                    font-weight: 800;
                    color: #323a45;
                }
                .factura-section-title .title-icon {
                    width: 26px;
                    height: 26px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(50,58,69,0.08);
                    color: #323a45;
                    font-size: 0.78rem;
                }
                .factura-total-label-cell {
                    border: none !important;
                    text-align: right;
                    vertical-align: middle !important;
                    padding: 3px 8px !important;
                    font-size: 0.8rem;
                    font-weight: 400;
                    color: #6c757d;
                    white-space: nowrap;
                    min-width: 96px;
                    line-height: 1.25;
                }
                .factura-total-value-cell {
                    border: none !important;
                    padding: 3px 0 !important;
                    vertical-align: middle !important;
                }
                .factura-total-value-input {
                    width: 100%;
                    max-width: 100%;
                    text-align: right;
                    font-size: 0.85rem;
                    font-weight: 400;
                    color: #495057;
                    background-color: #f8f9fa !important;
                    padding: 0.28rem 0.4rem !important;
                    min-height: 30px !important;
                }
                .factura-total-value-input.factura-total-final {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #17a2b8;
                }
                .factura-lineas-table {
                    table-layout: fixed;
                    border-collapse: collapse;
                }
                .factura-lineas-table th,
                .factura-lineas-table td {
                    border: none !important;
                }
                .factura-lineas-table thead th {
                    border-bottom: 1px solid #dee2e6 !important;
                    padding-bottom: 8px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6c757d;
                }
                .factura-lineas-table tfoot tr:first-child td {
                    border-top: 1px solid #e9ecef !important;
                    padding-top: 10px;
                }
                .factura-lineas-table .factura-col-descripcion {
                    text-align: center;
                    vertical-align: middle;
                    font-size: 0.875rem;
                    padding: 8px 10px;
                    line-height: 1.35;
                    word-break: break-word;
                }
                .factura-lineas-table .factura-col-cant,
                .factura-lineas-table .factura-col-desc {
                    width: 72px;
                    max-width: 72px;
                }
                .factura-lineas-table .factura-col-precio {
                    width: 118px;
                    max-width: 118px;
                }
                .factura-lineas-table .factura-input-cant,
                .factura-lineas-table .factura-input-desc {
                    width: 100% !important;
                    max-width: 64px !important;
                    min-width: 0 !important;
                    min-height: 32px !important;
                    padding: 0.28rem 0.35rem !important;
                    margin: 0 auto;
                    text-align: center;
                    -moz-appearance: textfield;
                }
                .factura-lineas-table .factura-input-precio {
                    width: 100% !important;
                    max-width: 110px !important;
                    min-height: 32px !important;
                    padding: 0.28rem 0.4rem !important;
                    text-align: right;
                    -moz-appearance: textfield;
                }
                .factura-lineas-table .factura-input-cant::-webkit-outer-spin-button,
                .factura-lineas-table .factura-input-cant::-webkit-inner-spin-button,
                .factura-lineas-table .factura-input-desc::-webkit-outer-spin-button,
                .factura-lineas-table .factura-input-desc::-webkit-inner-spin-button,
                .factura-lineas-table .factura-input-precio::-webkit-outer-spin-button,
                .factura-lineas-table .factura-input-precio::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
            `}</style>
            <Header />
            <Aside activelink="indicators" />
            
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Generar Factura desde RIPS</h1>
                            </div>
                            <div className="col-sm-6">
                                <Link to="/rips-convenios" className="btn btn-secondary float-right">
                                    <i className="fas fa-arrow-left"></i> Volver
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        
                        <div className="card card-info card-outline factura-soft-card">
                            <div className="card-header factura-soft-header">
                                <h5 className="factura-main-title">
                                    <span className="title-icon"><i className="fas fa-file-invoice-dollar"></i></span>
                                    Información de la Factura
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    
                                    <h5 className="factura-section-title">
                                        <span className="title-icon"><i className="fas fa-user"></i></span>
                                        Información del Paciente
                                    </h5>
                                    
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Nombre *</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={nombrePaciente}
                                                    readOnly
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Tipo *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={tipoPaciente}
                                                    readOnly
                                                    required
                                                >
                                                    <option value="CC">CC</option>
                                                    <option value="CE">CE</option>
                                                    <option value="PA">PA</option>
                                                    <option value="PE">PE</option>
                                                    <option value="RC">RC</option>
                                                    <option value="TI">TI</option>
                                                    <option value="NIT">NIT</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>N° Documento *</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={documentoPaciente}
                                                    readOnly
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-1">
                                            <div className="form-group">
                                                <label>&nbsp;</label>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-info form-control"
                                                    onClick={() => setShowModalPaciente(true)}
                                                >
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Responsabilidad *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={codigoResponsabilidad}
                                                    onChange={(e) => setCodigoResponsabilidad(e.target.value)}
                                                    required
                                                >
                                                    <option>No responsable</option>
                                                    <option>Régimen simple de tributación</option>
                                                    <option>Gran contribuyente</option>
                                                    <option>Autorretenedor</option>
                                                    <option>Agente de retención IVA</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Tipo de Régimen *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={codigoRegimen}
                                                    onChange={(e) => setCodigoRegimen(e.target.value)}
                                                    required
                                                >
                                                    <option>No responsable de IVA</option>
                                                    <option>Impuesto sobre las ventas / IVA</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label>Impuesto *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={codigoImpuesto}
                                                    onChange={(e) => setCodigoImpuesto(e.target.value)}
                                                    required
                                                >
                                                    <option>No causa</option>
                                                    <option>IVA</option>
                                                    <option>INC</option>
                                                    <option>IVA e INC</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <hr />

                                    <h5 className="factura-section-title">
                                        <span className="title-icon"><i className="fas fa-receipt"></i></span>
                                        Datos de Factura
                                    </h5>

                                    <div className="row">
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Fecha del Documento *</label>
                                                <input 
                                                    type="date" 
                                                    className="form-control" 
                                                    value={fecha}
                                                    onChange={(e) => {
                                                        setFecha(e.target.value);
                                                        if (tipoPago === 'contado') {
                                                            setVencimiento(e.target.value);
                                                        }
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Tipo de Pago *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={tipoPago}
                                                    onChange={(e) => {
                                                        setTipoPago(e.target.value);
                                                        if (e.target.value === 'contado') {
                                                            setVencimiento(fecha);
                                                        }
                                                    }}
                                                    required
                                                >
                                                    <option value="contado">Contado</option>
                                                    <option value="credito">Crédito</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Vencimiento *</label>
                                                <input 
                                                    type="date" 
                                                    className="form-control" 
                                                    value={vencimiento}
                                                    onChange={(e) => setVencimiento(e.target.value)}
                                                    disabled={tipoPago === 'contado'}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Profesional *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={profesional}
                                                    onChange={(e) => setProfesional(e.target.value)}
                                                    required
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    {profesionales.map(prof => (
                                                        <option key={prof.id} value={String(prof.id)}>{prof.name} {prof.lastname}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>Prefijo *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={prefijo}
                                                    onChange={handlePrefijoChange}
                                                    required
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    {getPrefijosFiltrados().map(p => (
                                                        <option key={p.id} value={String(p.id)}>{p.prefijo}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>N° Factura *</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={numeroFactura}
                                                    readOnly
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>Método de Pago *</label>
                                                <select 
                                                    className="form-control" 
                                                    value={metodoPago}
                                                    onChange={(e) => setMetodoPago(e.target.value)}
                                                    required
                                                >
                                                    <option value="10">Efectivo</option>
                                                    <option value="42">Consignación bancaria</option>
                                                    <option value="1">Instrumento no definido</option>
                                                    <option value="20">Cheque</option>
                                                    <option value="30">Transferencia Crédito</option>
                                                    <option value="31">Transferencia Débito</option>
                                                    <option value="45">Transferencia Crédito Bancario</option>
                                                    <option value="47">Transferencia Débito Bancaria</option>
                                                    <option value="48">Tarjeta Crédito</option>
                                                    <option value="49">Tarjeta Débito</option>
                                                    <option value="ZZZ">Acuerdo mutuo</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group">
                                                <label>N° de Contrato</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control"
                                                    value={contractNumber}
                                                    onChange={(e) => setContractNumber(e.target.value)}
                                                    placeholder="Opcional - Sector salud"
                                                    maxLength={50}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>N° de Póliza</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control"
                                                    value={policyNumber}
                                                    onChange={(e) => setPolicyNumber(e.target.value)}
                                                    placeholder="Opcional - Sector salud"
                                                    maxLength={50}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <hr />

                                    <h5><b>Servicios de RIPS</b></h5>

                                    <div className="table-responsive">
                                        <table className="table factura-lineas-table">
                                            <thead>
                                                <tr>
                                                    <th style={{width: '10%'}}>Código</th>
                                                    <th className="text-center factura-col-descripcion">Descripción</th>
                                                    <th className="text-center factura-col-cant">Cant.</th>
                                                    <th className="text-center factura-col-precio">Precio</th>
                                                    <th className="text-center factura-col-desc">% Desc</th>
                                                    <th style={{width: '9%', minWidth: '88px'}}>Ajuste</th>
                                                    <th style={{width: '10%'}}>Impuesto</th>
                                                    <th style={{width: '12%'}}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {productos.map(producto => (
                                                    <tr key={producto.id}>
                                                        <td>
                                                            <input 
                                                                type="text" 
                                                                className="form-control form-control-sm" 
                                                                value={producto.codigo}
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td className="factura-col-descripcion">{producto.descripcion}</td>
                                                        <td className="text-center factura-col-cant">
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-sm factura-input-cant" 
                                                                value={producto.cantidad}
                                                                onChange={(e) => actualizarProducto(producto.id, 'cantidad', e.target.value)}
                                                                min="1"
                                                                max="999"
                                                            />
                                                        </td>
                                                        <td className="text-right factura-col-precio">
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-sm factura-input-precio" 
                                                                value={producto.precio}
                                                                onChange={(e) => actualizarProducto(producto.id, 'precio', e.target.value)}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td className="text-center factura-col-desc">
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-sm factura-input-desc" 
                                                                value={producto.porcentaje}
                                                                onChange={(e) => actualizarProducto(producto.id, 'porcentaje', e.target.value)}
                                                                min="0"
                                                                max="100"
                                                            />
                                                        </td>
                                                        <td className="text-right">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={producto.ajuste ?? ''}
                                                                onChange={(e) => actualizarProducto(producto.id, 'ajuste', e.target.value)}
                                                                style={{textAlign: 'right'}}
                                                                min={-calcularBaseConDescuentoItem(producto)}
                                                                max={(parseFloat(producto.precio) || 0) * (parseFloat(producto.cantidad) || 1)}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <select 
                                                                className="form-control form-control-sm" 
                                                                value={producto.impuesto}
                                                                onChange={(e) => actualizarProducto(producto.id, 'impuesto', e.target.value)}
                                                                style={{textAlign: 'center', textAlignLast: 'center', maxWidth: '120px', margin: '0 auto'}}
                                                            >
                                                                <option value="0">0</option>
                                                                {impuestos.map(imp => (
                                                                    <option key={imp.id} value={imp.impuesto}>{imp.nombre+' '+imp.impuesto+'%'}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="text-right">
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-sm" 
                                                                value={parseFloat(producto.total || 0).toFixed(2)}
                                                                readOnly
                                                                style={{textAlign: 'right'}}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6" style={{border: 'none'}}></td>
                                                    <td className="factura-total-label-cell">Subtotal:</td>
                                                    <td className="factura-total-value-cell">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm factura-total-value-input" 
                                                            value={formatearValor(totalBruto)}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{border: 'none'}}></td>
                                                    <td className="factura-total-label-cell">Descuento{'\u00A0'}:</td>
                                                    <td className="factura-total-value-cell">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm factura-total-value-input" 
                                                            value={formatearValor(totalDescuento)}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{border: 'none'}}></td>
                                                    <td className="factura-total-label-cell">Ajuste:</td>
                                                    <td className="factura-total-value-cell">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm factura-total-value-input" 
                                                            value={'$ ' + productos.reduce((sum, p) => sum + (parseFloat(p.ajuste) || 0), 0).toLocaleString('es-CO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{border: 'none'}}></td>
                                                    <td className="factura-total-label-cell">IVA:</td>
                                                    <td className="factura-total-value-cell">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm factura-total-value-input" 
                                                            value={formatearValor(totalIVA)}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" style={{border: 'none'}}></td>
                                                    <td className="factura-total-label-cell">Total:</td>
                                                    <td className="factura-total-value-cell">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm factura-total-value-input factura-total-final" 
                                                            value={formatearValor(totalPagar)}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <hr />

                                    <div className="form-group">
                                        <label>Observaciones</label>
                                        <textarea 
                                            className="form-control" 
                                            rows="3"
                                            value={observaciones}
                                            onChange={(e) => setObservaciones(e.target.value)}
                                            placeholder="Factura generada desde RIPS por convenio"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <button type="submit" className="btn btn-success btn-lg">
                                            <i className="fas fa-save"></i> Generar Factura
                                        </button>
                                        <Link to="/rips-convenios" className="btn btn-secondary btn-lg ml-2">
                                            <i className="fas fa-times"></i> Cancelar
                                        </Link>
                                    </div>

                                </form>
                            </div>
                        </div>

                    </div>
                </section>
            </div>

            <Footer />

            {/* Modal para seleccionar paciente */}
            {showModalPaciente && (
                <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Seleccionar Paciente</h5>
                                <button type="button" className="close" onClick={() => setShowModalPaciente(false)}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group mb-3">
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="Buscar por nombre o documento..."
                                        value={busquedaPaciente}
                                        onChange={(e) => {
                                            setBusquedaPaciente(e.target.value);
                                            setPaginaActual(1);
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        height: 'min(52vh, 420px)',
                                        overflowY: 'scroll',
                                        scrollbarGutter: 'stable',
                                    }}
                                >
                                    <table className="table table-hover table-sm table-borderless mb-0">
                                        <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Documento</th>
                                                <th className="text-center" style={{ width: '56px' }}>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getPacientesPaginados().map((paciente) => (
                                                <tr key={paciente.id ?? `${paciente.numero_documento}-${getNombreCliente(paciente)}`}>
                                                    <td>{getNombreCliente(paciente)}</td>
                                                    <td>{paciente.numero_documento}</td>
                                                    <td className="text-center align-middle">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-sm btn-success"
                                                            title="Seleccionar paciente"
                                                            aria-label="Seleccionar paciente"
                                                            onClick={() => seleccionarPaciente(paciente)}
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {getPacientesPaginados().length === 0 && (
                                                <tr>
                                                    <td colSpan="3" className="text-center text-muted py-4">
                                                        No hay resultados para la búsqueda.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Paginación */}
                                {getTotalPaginas() > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary btn-sm"
                                            disabled={paginaActual === 1}
                                            onClick={() => setPaginaActual(paginaActual - 1)}
                                        >
                                            <i className="fas fa-chevron-left"></i> Anterior
                                        </button>
                                        <span className="badge badge-info">Página {paginaActual} de {getTotalPaginas()}</span>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary btn-sm"
                                            disabled={paginaActual === getTotalPaginas()}
                                            onClick={() => setPaginaActual(paginaActual + 1)}
                                        >
                                            Siguiente <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModalPaciente(false)}>
                                    <i className="fas fa-times"></i> Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Si no tiene permisos, mostrar página de no autorizado
    if (hasPermission === false) {
        return <Unauthorized />;
    }
}

export default CrearFacturaRips;
